import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight, LayoutDashboard, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const portalPath = user ? (["admin", "processor", "loan_officer"].includes(user.role) ? "/admin" : "/portal") : "/login";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-obsidian/95 border-b border-edge/80 backdrop-blur-xl" : "bg-obsidian/60 border-b border-transparent backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between">
          <Logo size={52} showText={false} />

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-${l.path.replace(/\//g, "")}-link`}
                className={`text-sm font-semibold tracking-wide transition-colors hover:text-gold ${
                  location.pathname === l.path ? "text-gold" : "text-white/80"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Button variant="ghost" onClick={() => navigate(portalPath)} data-testid="nav-portal-button"
                  className="text-white/85 hover:text-gold hover:bg-transparent gap-2">
                  <LayoutDashboard className="w-4 h-4" /> {["admin","processor","loan_officer"].includes(user.role) ? "Admin" : "My Portal"}
                </Button>
                <Button variant="ghost" onClick={logout} data-testid="nav-logout-button" className="text-white/60 hover:text-brandred hover:bg-transparent">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-button"
                className="text-white/85 hover:text-gold hover:bg-transparent">
                Sign In
              </Button>
            )}
            <Button onClick={() => navigate("/apply")} data-testid="nav-submit-deal-button"
              className="btn-gold rounded-full px-6 h-11 gap-1">
              Apply for Financing <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-obsidian border-t border-edge animate-fade-in" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <Link key={l.path} to={l.path} className="block px-3 py-3 rounded-lg text-white/85 hover:bg-surface hover:text-gold font-semibold">
                {l.label}
              </Link>
            ))}
            <div className="pt-3 space-y-2">
              <Button onClick={() => navigate("/apply")} className="btn-gold w-full rounded-full h-12" data-testid="mobile-submit-deal-button">
                Apply for Financing
              </Button>
              <Button variant="outline" onClick={() => navigate(user ? portalPath : "/login")} className="btn-outline-glass w-full rounded-full h-12">
                {user ? "Go to Portal" : "Sign In"}
              </Button>
              {user && (
                <Button variant="ghost" onClick={logout} className="w-full text-white/60">Sign Out</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
